import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../components/button';
import {
  ExpenseFilter,
  ExpenseType,
  emptyExpenseFilter,
  Expense,
} from '../types';
import { Option } from './types';
import { createExpenseStatusOptions } from './mappings';
import ClearFiltersLink from '../../../components/ClearFiltersLink';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import './ExpensesFilterTiles.css';
import { ExpenseGroupType } from '../../../__generated__/graphql';

type Props = {
  filter: ExpenseFilter;
  yearOptions: Option[];
  categoryOptions: Option[];
  expenses: Expense[];
  expenseGroups: any[];
  onChange: (filter: ExpenseFilter) => void;
  onCreateBtnClick?: () => void;
  onCreateExpenseGroupBtnClick?: () => void;
};

type FilterTileProps = {
  label: string;
  value: string | null;
  options: Option[];
  placeholder: string;
  icon: string;
  onChange: (value: string | null) => void;
  isSearchable?: boolean;
};

const FilterTile: React.FC<FilterTileProps> = ({
  label,
  value,
  options,
  placeholder,
  icon,
  onChange,
  isSearchable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTileClick = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (optionValue: string) => {
    onChange(optionValue === value ? null : optionValue);
    setIsOpen(false);
  };

  return (
    <div className="filter-tile-clickable" ref={dropdownRef}>
      <button
        className={`filter-tile-button ${value ? 'has-value' : ''}`}
        onClick={handleTileClick}>
        <FlairIcon icon={icon as any} size="sm" />
        <span className="filter-tile-text">{displayText}</span>
        <FlairIcon
          icon={isOpen ? 'chevron-up-outline' : 'chevron-forward-outline'}
          size="xs"
          className="filter-tile-arrow"
        />
      </button>

      {isOpen && (
        <div className="filter-dropdown-menu">
          <div className="filter-dropdown-list">
            {options.map((option) => (
              <button
                key={option.value}
                className={`filter-dropdown-option ${
                  value === option.value ? 'selected' : ''
                }`}
                onClick={() => handleOptionClick(option.value)}>
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const ExpensesFilterTiles: React.FC<Props> = ({
  filter,
  yearOptions,
  categoryOptions,
  expenses,
  expenseGroups,
  onChange,
  onCreateBtnClick,
  onCreateExpenseGroupBtnClick,
}) => {
  const { t } = useTranslation();

  const handleClearFilter = useCallback(() => {
    onChange(emptyExpenseFilter);
  }, [onChange]);

  const handleFilterChange = useCallback(
    (filterKey: keyof ExpenseFilter, value: string | null) => {
      onChange({
        ...filter,
        [filterKey]: value,
      });
    },
    [filter, onChange],
  );

  const getTypeOptions = (): Option[] => {
    const individualCount = expenses.filter(
      (e) => !e.flair__Expense_Group__c,
    ).length;
    const groupsCount = expenseGroups.filter(
      (g) => g.flair__Type__c !== ExpenseGroupType.Trip,
    ).length;
    const tripsCount = expenseGroups.filter(
      (g) => g.flair__Type__c === ExpenseGroupType.Trip,
    ).length;

    return [
      {
        label: t('expenses.filters.individual', { count: individualCount }),
        value: 'individual',
      },
      {
        label: t('expenses.filters.group', { count: groupsCount }),
        value: 'groups',
      },
      {
        label: t('expenses.filters.trip', { count: tripsCount }),
        value: 'trips',
      },
    ];
  };

  const statusOptions = createExpenseStatusOptions();

  return (
    <div className="expenses-filter-tiles-enhanced">
      <div className="filter-tiles-container">
        <div className="filter-tiles-wrapper">
          <FilterTile
            label={t('expenses.filters.type')}
            value={filter.type}
            options={getTypeOptions()}
            placeholder={t('expenses.filters.allTypes')}
            icon="folder-outline"
            onChange={(value) =>
              handleFilterChange('type', value as ExpenseType)
            }
          />
          <FilterTile
            label={t('expenses.filters.status')}
            value={filter.status}
            options={statusOptions}
            placeholder={t('expenses.filters.allStatuses')}
            icon="checkmark-outline"
            onChange={(value) => handleFilterChange('status', value)}
          />
          {yearOptions.length > 0 && (
            <FilterTile
              label={t('expenses.filters.year')}
              value={filter.year}
              options={yearOptions}
              placeholder={t('expenses.filters.allYears')}
              icon="calendar-clear-outline"
              onChange={(value) => handleFilterChange('year', value)}
              isSearchable={true}
            />
          )}
          {categoryOptions.length > 0 && (
            <FilterTile
              label={t('expenses.filters.category')}
              value={filter.category}
              options={categoryOptions}
              placeholder={t('expenses.filters.allCategories')}
              icon="document-outline"
              onChange={(value) => handleFilterChange('category', value)}
              isSearchable={true}
            />
          )}
          <div className="clear-filters-tile">
            <ClearFiltersLink onClick={handleClearFilter} />
          </div>
        </div>
        {(onCreateExpenseGroupBtnClick || onCreateBtnClick) && (
          <div className="action-buttons-enhanced">
            {onCreateExpenseGroupBtnClick && (
              <Button
                size="sm"
                variant="outline-primary"
                onClick={onCreateExpenseGroupBtnClick}
                label={t('expenses.newExpenseGroup')}
              />
            )}
            {onCreateBtnClick && (
              <Button
                size="sm"
                variant="primary"
                onClick={onCreateBtnClick}
                label={t('expenses.newExpense')}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};
